import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createCloudRunAxios } from '../../../api/axios';

const remiApi = createCloudRunAxios('remi-api');

export interface DatabaseUploadRequest {
  id: string;
  orgId: string;
  orgName: string;
  userId: string;
  userEmail: string;
  aiOptedIn: 'yes' | 'no';
  flowTypes: string[];
  tags: string[];
  assignedTo: { name: string; phone: string } | null;
  useAssignment: boolean;
  assignmentAction: 'assign' | 'unassigned' | '';
  assignedAgents: string[];
  fileUrl: string;
  errorFilePath: string;
  errorFileName: string;
  fileName: string;
  status: 'pending' | 'processing' | 'complete';
  createdAt: unknown;
  [key: string]: unknown;
}

interface ImportSettings {
  aiOptedIn?: 'yes' | 'no';
  flowTypes?: string[];
  tags?: string[];
  assignedTo?: { name: string; phone: string } | null;
  useAssignment?: boolean;
  assignmentAction?: 'assign' | 'unassigned' | '';
  assignedAgents?: string[];
  fileUrl?: string;
  orgName?: string;
  userEmail?: string;
  userId?: string;
  [key: string]: unknown;
}

interface RawUploadRequest {
  request_id?: string;
  id?: string;
  organization_id?: string;
  requested_by_user_id?: string;
  file_name?: string;
  error_file_path?: string;
  error_file_name?: string;
  status?: 'pending' | 'processing' | 'complete';
  created_at?: unknown;
  import_settings?: ImportSettings;
  [key: string]: unknown;
}

function mapRequest(raw: RawUploadRequest): DatabaseUploadRequest {
  const s = raw.import_settings ?? {};
  return {
    id: (raw.request_id ?? raw.id ?? '') as string,
    orgId: (raw.organization_id as string) ?? '',
    orgName: s.orgName ?? '',
    userId: (raw.requested_by_user_id as string) ?? s.userId ?? '',
    userEmail: s.userEmail ?? '',
    aiOptedIn: s.aiOptedIn ?? 'no',
    flowTypes: s.flowTypes ?? [],
    tags: s.tags ?? [],
    assignedTo: s.assignedTo ?? null,
    useAssignment: s.useAssignment ?? false,
    assignmentAction: s.assignmentAction ?? '',
    assignedAgents: s.assignedAgents ?? [],
    fileUrl: s.fileUrl ?? '',
    errorFilePath: raw.error_file_path ?? '',
    errorFileName: raw.error_file_name ?? '',
    status: raw.status ?? 'pending',
    createdAt: raw.created_at,
    fileName: raw.file_name ?? '',
  };
}

export const useDatabaseUploadRequests = (orgId: string | undefined) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['database-upload-requests', orgId],
    queryFn: async () => {
      const response = await remiApi.get<{ items: RawUploadRequest[] } | RawUploadRequest[]>(
        `/api/v1/organizations/${orgId}/database-uploads`
      );
      const items = Array.isArray(response.data)
        ? response.data
        : (response.data as { items: RawUploadRequest[] }).items ?? [];
      return items.map(mapRequest);
    },
    enabled: !!orgId,
  });

  return {
    data: data ?? ([] as DatabaseUploadRequest[]),
    isLoading,
    error: error as Error | null,
    refetch,
  };
};

export const downloadDatabaseUploadErrorFile = async (
  orgId: string,
  requestId: string,
  fileName: string
) => {
  const response = await remiApi.get(
    `/api/v1/organizations/${orgId}/database-uploads/${requestId}/error-file`,
    { responseType: 'blob' }
  );
  const objectUrl = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName || `database-upload-errors-${requestId}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

export const useUpdateDatabaseUploadStatus = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: async ({ orgId, requestId, newStatus }: { orgId: string; requestId: string; newStatus: string; currentUploads?: unknown[] }) => {
      const response = await remiApi.patch(
        `/api/v1/organizations/${orgId}/database-uploads/${requestId}`,
        { status: newStatus }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['database-upload-requests', variables.orgId] });
    },
  });

  return {
    mutate,
    mutateAsync,
    isPending,
    isError,
    error: error as Error | null,
  };
};
